import React, { useEffect, useMemo, useState } from 'react';
import { useAppConfig } from '../context/AppConfigContext';
import {
  CarouselLogo,
  fetchCarouselLogos,
  getDefaultCarouselLogos,
} from '../services/carouselLogos';

const LogoCarousel: React.FC = () => {
  const { config } = useAppConfig();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [remoteLogos, setRemoteLogos] = useState<CarouselLogo[]>([]);
  const [loading, setLoading] = useState(true);

  const interval = config.carouselInterval;

  useEffect(() => {
    const loadCarouselLogos = async () => {
      try {
        const fetchedLogos = await fetchCarouselLogos();
        setRemoteLogos(fetchedLogos.length > 0 ? fetchedLogos : getDefaultCarouselLogos());
      } catch (error) {
        console.warn(
          'Failed to load carousel logos from Firestore. Falling back to local assets.',
          error
        );
        setRemoteLogos(getDefaultCarouselLogos());
      } finally {
        setLoading(false);
      }
    };

    loadCarouselLogos();
  }, []);

  const logos = useMemo(() => {
    return remoteLogos.map((item) => ({
      src: item.logoUrl,
      alt: item.name || item.id,
      width: item.displayWidth,
      height: item.displayHeight,
    }));
  }, [remoteLogos]);

  useEffect(() => {
    if (logos.length <= 1) {
      return;
    }

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % logos.length);
    }, interval);

    return () => clearInterval(timer);
  }, [interval, logos.length]);

  useEffect(() => {
    if (currentIndex >= logos.length) {
      setCurrentIndex(0);
    }
  }, [currentIndex, logos.length]);

  if (loading) {
    return <div className="w-full h-72 mt-6 mb-10" />;
  }

  return (
    <div className="w-full h-72 overflow-hidden mt-6 mb-10">
      <div
        className="flex transition-transform duration-1000 ease-in-out h-full"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {logos.map((logo, index) => (
          <div
            key={`${logo.alt}-${index}`}
            className="flex-shrink-0 w-full flex items-center justify-center"
          >
            <div
              className="flex items-center justify-center"
              style={{
                width: logo.width ? `${logo.width}px` : '400px',
                height: logo.height ? `${logo.height}px` : '180px',
                maxWidth: 'calc(100vw - 48px)',
              }}
            >
              <img
                src={logo.src}
                alt={logo.alt}
                className="block"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
                loading="lazy"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LogoCarousel;
